'use client';

import { useState } from 'react';
import { Footprints, PlayCircle, ListChecks, Sparkles, Star, Target, X, ArrowRight, Lock, MessageCircle, GraduationCap, FlaskConical, RefreshCw, Pencil, Volume2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '@/lib/authService';
import { useGuidedTour } from './GuidedTourProvider';
import { useAdaptiveFlowResolver, getFlowBadge } from './ai/AdaptiveFlowResolver';
import { useTourMascot } from './hooks/useTourMascot';
import { useTourSandbox } from './hooks/useTourSandbox';
import { enterTrainingSandbox } from './utils/enterTrainingSandbox';
import { isTourMediaRegenAvailable, openTourMediaManager, requestTourMediaRegen } from './utils/tourMediaRegen';
import { resolveFlowFormats, TOUR_FORMATS, type TourFormat } from './utils/formatResolver';
import { TOUR_VIDEO_LIBRARY_EVENT, type TourVideoLibraryDetail } from './GuidedTourVideoLibrary';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface GuidedTourIndexProps {
  onSelectFlow?: (flowId: string, format?: TourFormat) => void;
}

export function GuidedTourIndex({ onSelectFlow }: GuidedTourIndexProps) {
  const { isOpen, moduleKey, role, flows, engine, config, progress, schema, objectives, scopedFlowIds, openTour } = useGuidedTour();

  const adaptiveEnabled = schema?.ai?.adaptive_flows ?? false;
  const completedFlowIds = progress.progress.completed_flow_ids ?? [];
  const hasObjectives = objectives.length > 0;
  const moduleColor = config.moduleConfig?.module_color ?? '#6366f1';

  const { recommendations, reorderedFlows, loading: recLoading } = useAdaptiveFlowResolver({
    flows,
    role,
    moduleKey,
    enabled: adaptiveEnabled && isOpen,
    completedFlowIds,
  });

  // Sandbox entry — offered when the global sandbox config enables this
  // module. The shared helper performs the lease + demo-context swap and
  // reloads; a pending open stash brings the tour index back up afterwards.
  const sandboxModules = schema?.sandbox?.modules;
  const sandboxCapable = Boolean(
    schema?.sandbox?.enabled &&
    (Array.isArray(sandboxModules)
      ? sandboxModules.includes(moduleKey)
      : sandboxModules?.[moduleKey])
  );
  const tourSandbox = useTourSandbox({ moduleKey });
  const [sandboxEntering, setSandboxEntering] = useState(false);

  // Dev+root only: on-demand media regeneration. Nothing regenerates on a
  // schedule anymore — a flow's video/audio is only re-rendered when a root
  // user explicitly asks for it here.
  const mediaRegenAvailable = isTourMediaRegenAvailable();
  const [regenFlowId, setRegenFlowId] = useState<string | null>(null);

  // Dev+root: launch a flow straight into edit mode — the runner opens the
  // tour's intro card with the inline editor already up, so the author edits
  // where the tour actually runs (no Shelly's Studio navigation).
  const startFlowForEdit = (flowId: string) => {
    try {
      sessionStorage.setItem('guided-tour:edit-intent', 'flow');
    } catch { /* non-fatal — editor just won't auto-open */ }
    openTour({ flowId, format: 'interactive' });
  };



  // Load the mascot in index mode so we can display the intro pose outside
  // the card to draw attention to the tour panel.
  const { currentPoseAsset, defaultPoseUrl, assets, choreography } = useTourMascot({
    currentStep: null,
    currentStepIndex: 0,
    isOpen,
    isIndexMode: true,
    mascotConfig: schema?.mascot ?? null,
  });

  const poseUrl = currentPoseAsset?.file_url ?? defaultPoseUrl ?? assets[0]?.file_url ?? undefined;

  // Context-scoped mounts (modals, designers) only surface their own flows.
  const visibleFlows = scopedFlowIds
    ? flows.filter((flow) => scopedFlowIds.includes(flow.id))
    : flows;

  const completedCount = visibleFlows.filter((flow) => completedFlowIds.includes(flow.id)).length;
  const totalCount = visibleFlows.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // ── Index edit mode (dev + root) ─────────────────────────────────────
  // Turns the index card itself into the editor: the module name/colour and
  // every flow's index-facing label + summary become inline fields that
  // PATCH straight into the module YAML — no navigation away from the page.
  const [indexEditMode, setIndexEditMode] = useState(false);
  const [indexDrafts, setIndexDrafts] = useState<Record<string, { label: string; summary: string }>>({});
  const [moduleDraft, setModuleDraft] = useState({ module_name: '', module_color: '' });
  const [indexSaving, setIndexSaving] = useState<string | null>(null);

  const toggleIndexEdit = () => {
    if (!indexEditMode) {
      const drafts: Record<string, { label: string; summary: string }> = {};
      for (const f of visibleFlows) {
        drafts[f.id] = { label: f.label ?? '', summary: f.summary ?? '' };
      }
      setIndexDrafts(drafts);
      setModuleDraft({
        module_name: config.moduleConfig?.module_name ?? '',
        module_color: config.moduleConfig?.module_color ?? '',
      });
    }
    setIndexEditMode((v) => !v);
  };

  const indexPatch = async (saveKey: string, body: Record<string, unknown>) => {
    setIndexSaving(saveKey);
    try {
      const token = authService.getTokens()?.access_token;
      const res = await fetch(`/api/v1/guided-tours/modules/${moduleKey}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        throw new Error(json?.error ?? `HTTP ${res.status}`);
      }
      toast.success('Saved — tour YAML updated');
      config.refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Save failed');
    } finally {
      setIndexSaving(null);
    }
  };

  if (!isOpen || engine.viewMode !== 'index' || engine.isMinimized) {
    return null;
  }

  const formatIcon = (icon: string) => {
    switch (icon) {
      case 'play-circle':
        return <PlayCircle className="h-3 w-3" />;
      case 'list-checks':
        return <ListChecks className="h-3 w-3" />;
      case 'footprints':
      default:
        return <Footprints className="h-3 w-3" />;
    }
  };

  const orderedFlows = adaptiveEnabled ? reorderedFlows.filter((flow) => visibleFlows.includes(flow)) : visibleFlows;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[70] flex items-end gap-2">
      {/* Mascot — positioned outside and to the left of the card, pointing right.
          Uses CSS animation for the entrance (no state needed). */}
      <div
        className="pointer-events-none relative shrink-0 animate-[tour-mascot-pop_0.5s_ease-out_forwards]"
        style={{ marginBottom: '0.5rem' }}
      >
        {poseUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={poseUrl}
            alt="Tour guide mascot"
            className="h-28 w-28 object-contain drop-shadow-2xl sm:h-36 sm:w-36"
            style={choreography.mascotImageStyle}
          />
        ) : (
          <div
            className="flex h-28 w-28 items-center justify-center rounded-full sm:h-36 sm:w-36"
            style={{ backgroundColor: `${moduleColor}20` }}
          >
            <Sparkles className="h-10 w-10" style={{ color: moduleColor }} />
          </div>
        )}
        {/* Speech bubble tail pointing toward the card */}
        <div className="absolute -right-1 top-1/2 hidden -translate-y-1/2 sm:block">
          <div
            className="h-0 w-0 border-y-8 border-l-8 border-y-transparent"
            style={{ borderLeftColor: 'rgb(255 255 255 / 0.95)' }}
          />
        </div>
      </div>

      {/* Tour card — slides in with a slight delay via CSS animation.
          Same width as the unified chat panel so toggling between the index
          and chat never shifts the floating surface horizontally. */}
      <div className="pointer-events-auto w-[min(92vw,26rem)] animate-[tour-card-slide_0.5s_ease-out_0.15s_both]">
        <Card
          className="overflow-hidden border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-800/95"
          style={{ borderTopColor: moduleColor, borderTopWidth: '3px' }}
        >
          {/* Header with gradient accent */}
          <div
            className="flex items-start justify-between gap-3 px-4 pb-2 pt-3"
            style={{
              background: `linear-gradient(135deg, ${moduleColor}10, transparent)`,
            }}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 shrink-0" style={{ color: moduleColor }} />
                <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">
                  Guided Tours
                </h2>
              </div>
              <p className="mt-0.5 truncate text-xs font-medium capitalize text-slate-500 dark:text-slate-400">
                {config.moduleConfig?.module_name ?? moduleKey}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {/* Dev+root: edit the index itself — module name/colour and
                  every card's label + summary become inline fields that
                  patch the module YAML without leaving the page. */}
              {mediaRegenAvailable && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn('h-7 gap-1 px-2 text-xs', indexEditMode && 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300')}
                  onClick={toggleIndexEdit}
                  title={indexEditMode ? 'Done editing' : 'Edit tour index items (dev only)'}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
              )}
              {/* Dev+root: media manager — every step's audio file, its age
                  and length, per-flow video status, and BullMQ-backed
                  regeneration (single scope, whole module, or "fill missing")
                  with live progress. */}
              {mediaRegenAvailable && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs"
                  onClick={() => openTourMediaManager({ moduleKey })}
                  title="Manage tour audio & video (dev only)"
                >
                  <Volume2 className="h-3.5 w-3.5" />
                  Media
                </Button>
              )}
              {hasObjectives && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs"
                  onClick={engine.showObjectiveIndex}
                  title="Browse practice objectives"
                >
                  <Target className="h-3.5 w-3.5" />
                  Objectives
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs"
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent(TOUR_VIDEO_LIBRARY_EVENT, {
                      detail: { moduleKey } satisfies TourVideoLibraryDetail,
                    })
                  )
                }
                title="Browse all video guides"
              >
                <PlayCircle className="h-3.5 w-3.5" />
                Videos
              </Button>

              {/* Switch to chat — the unified AI surface (module assistant +
                  training coach share one chat; the chat's "Tours" button
                  returns here). */}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs"
                onClick={() => engine.selectChat()}
                title="Switch to chat — AI assistant and training coach"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                Chat
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={engine.minimize}
                title="Minimize tours"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Progress bar */}
          {totalCount > 0 && (
            <div className="px-4 pb-2">
              <div className="mb-1 flex items-center justify-between text-[10px] font-medium text-slate-400 dark:text-slate-500">
                <span>{completedCount} of {totalCount} completed</span>
                <span>{progressPercent}%</span>
              </div>
              <div className="h-1 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${progressPercent}%`,
                    backgroundColor: moduleColor,
                  }}
                />
              </div>
            </div>
          )}

          {/* Training sandbox — entry when inactive, status when active */}
          {sandboxCapable && !tourSandbox.isSandboxActive && (
            <div className="px-3 pb-2">
              <button
                type="button"
                disabled={sandboxEntering || tourSandbox.loading}
                onClick={() => {
                  setSandboxEntering(true);
                  void enterTrainingSandbox({ moduleKey }).catch(() => setSandboxEntering(false));
                }}
                className="flex w-full items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-left transition-colors hover:bg-amber-100 disabled:opacity-60 dark:border-amber-800/60 dark:bg-amber-900/20 dark:hover:bg-amber-900/30"
                data-tour-id="tour-index-sandbox-enter"
              >
                <FlaskConical className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-amber-800 dark:text-amber-200">
                    {sandboxEntering || tourSandbox.loading ? 'Entering sandbox…' : 'Practice in the Training Sandbox'}
                  </span>
                  <span className="block text-[10px] leading-snug text-amber-700/80 dark:text-amber-300/80">
                    A demo client with realistic fake data — nothing affects your real work.
                  </span>
                </span>
                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
              </button>
            </div>
          )}
          {sandboxCapable && tourSandbox.isSandboxActive && (
            <div className="px-3 pb-2">
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 dark:border-emerald-800/60 dark:bg-emerald-900/20">
                <GraduationCap className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <span className="text-[11px] font-medium text-emerald-800 dark:text-emerald-200">
                  Sandbox active — you&apos;re practising on demo data.
                </span>
              </div>
            </div>
          )}

          <CardContent className="max-h-[55vh] space-y-2 overflow-y-auto p-3 pt-1">
            {indexEditMode && (
              <div className="space-y-1.5 rounded-lg border border-indigo-200 bg-indigo-50/50 p-2.5 dark:border-indigo-800/60 dark:bg-indigo-900/20">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-300">
                    Module details
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[10px]"
                    disabled={indexSaving === 'module'}
                    onClick={() =>
                      void indexPatch('module', {
                        patch: {
                          module_name: moduleDraft.module_name.trim() || null,
                          module_color: moduleDraft.module_color.trim() || null,
                        },
                      })
                    }
                  >
                    {indexSaving === 'module' ? 'Saving…' : 'Save module'}
                  </Button>
                </div>
                <input
                  type="text"
                  value={moduleDraft.module_name}
                  onChange={(e) => setModuleDraft((d) => ({ ...d, module_name: e.target.value }))}
                  placeholder="Module display name"
                  className="w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                />
                <input
                  type="text"
                  value={moduleDraft.module_color}
                  onChange={(e) => setModuleDraft((d) => ({ ...d, module_color: e.target.value }))}
                  placeholder="Accent colour (e.g. #4ecdc4)"
                  className="w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                />
              </div>
            )}
            {visibleFlows.length === 0 && (
              <div className="py-6 text-center">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No tours are available for this module yet.
                </p>
              </div>
            )}

            {recLoading && (
              <p className="py-2 text-center text-xs text-slate-400 dark:text-slate-500">
                Personalising recommendations…
              </p>
            )}

            {orderedFlows.map((flow, index) => {
              const resolution = resolveFlowFormats(flow);
              const isStarted = progress.progress.last_active_flow_id === flow.id;
              const isCompleted = completedFlowIds.includes(flow.id);
              const badge = getFlowBadge(flow.id, recommendations);
              const isRecommended = badge?.recommended;

              // Prerequisite checking — a flow is locked if any of its
              // prerequisite flow IDs are not in the completed list.
              const prerequisites = flow.prerequisites ?? [];
              const missingPrereqs = prerequisites.filter((id) => !completedFlowIds.includes(id));
              const isLocked = missingPrereqs.length > 0;
              const prereqLabels = missingPrereqs
                .map((id) => flows.find((f) => f.id === id)?.label ?? id)
                .join(', ');

              return (
                <div
                  key={flow.id}
                  className={cn(
                    'group relative overflow-hidden rounded-xl border p-3 transition-all duration-200',
                    'border-slate-200/70 bg-white hover:border-slate-300 hover:shadow-md',
                    'dark:border-slate-700/60 dark:bg-slate-800/40 dark:hover:border-slate-600',
                    isRecommended && 'border-violet-200 ring-1 ring-violet-200/50 dark:border-violet-800/60 dark:ring-violet-800/30',
                    isCompleted && 'opacity-70'
                  )}
                >
                  {/* Left accent bar */}
                  <div
                    className="absolute left-0 top-0 h-full w-1"
                    style={{
                      backgroundColor: isCompleted ? '#22c55e' : moduleColor,
                      opacity: isCompleted ? 0.6 : 0.8,
                    }}
                  />

                  <div className="flex items-start justify-between gap-2 pl-1.5">
                    <div className="min-w-0 flex-1">
                      {indexEditMode ? (
                        <div className="space-y-1">
                          <input
                            type="text"
                            value={indexDrafts[flow.id]?.label ?? ''}
                            onChange={(e) =>
                              setIndexDrafts((d) => ({
                                ...d,
                                [flow.id]: { label: e.target.value, summary: d[flow.id]?.summary ?? flow.summary ?? '' },
                              }))
                            }
                            placeholder="Tour name"
                            className="w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                          />
                          <textarea
                            rows={2}
                            value={indexDrafts[flow.id]?.summary ?? ''}
                            onChange={(e) =>
                              setIndexDrafts((d) => ({
                                ...d,
                                [flow.id]: { label: d[flow.id]?.label ?? flow.label ?? '', summary: e.target.value },
                              }))
                            }
                            placeholder="Card summary"
                            className="w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs leading-relaxed text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                          />
                        </div>
                      ) : (
                      <>
                      <div className="flex items-center gap-1.5">
                        <span
                          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[10px] font-bold text-white"
                          style={{ backgroundColor: isLocked ? '#94a3b8' : moduleColor }}
                        >
                          {isLocked ? <Lock className="h-2.5 w-2.5" /> : index + 1}
                        </span>
                        <h3 className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {flow.label}
                        </h3>
                        {mediaRegenAvailable && (flow.steps?.length ?? 0) > 0 && (
                          <span className="shrink-0 text-[10px] text-slate-400 dark:text-slate-500">
                            {flow.steps.length} step{flow.steps.length === 1 ? '' : 's'}
                          </span>
                        )}
                        {isRecommended && !isLocked && (
                          <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                            <Star className="h-2.5 w-2.5 fill-violet-400 text-violet-400 dark:fill-violet-300 dark:text-violet-300" />
                            Recommended
                          </span>
                        )}
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                        {flow.summary}
                      </p>
                      </>
                      )}
                      {isLocked && (
                        <p className="mt-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                          Complete first: {prereqLabels}
                        </p>
                      )}
                      {badge?.rationale && !isLocked && (
                        <p className="mt-0.5 text-[10px] italic text-violet-500 dark:text-violet-300">
                          {badge.rationale}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {isCompleted && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
                          <Star className="h-2.5 w-2.5 fill-green-500 text-green-500" />
                          Done
                        </span>
                      )}
                      {isStarted && !isCompleted && !isLocked && (
                        <span className="rounded-full px-2 py-0.5 text-[10px] font-medium text-white" style={{ backgroundColor: moduleColor }}>
                          Resume
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-2.5 flex flex-wrap gap-1.5 pl-1.5">
                    {indexEditMode && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1 px-2.5 text-[11px] font-medium dark:border-slate-600 dark:text-slate-200"
                        disabled={indexSaving === flow.id}
                        onClick={() =>
                          void indexPatch(flow.id, {
                            flowId: flow.id,
                            patch: {
                              label: indexDrafts[flow.id]?.label?.trim() || null,
                              summary: indexDrafts[flow.id]?.summary?.trim() || null,
                            },
                          })
                        }
                      >
                        {indexSaving === flow.id ? 'Saving…' : 'Save card'}
                      </Button>
                    )}
                    {isLocked ? (
                      <p className="text-[10px] text-slate-400 dark:text-slate-500">
                        Complete the prerequisite tour{missingPrereqs.length > 1 ? 's' : ''} to unlock.
                      </p>
                    ) : (
                    resolution.availableFormats.map((format) => {
                      const info = TOUR_FORMATS.find((f) => f.id === format)!;
                      const isPrimaryFormat = format === resolution.availableFormats[0];
                      return (
                        <Button
                          key={format}
                          variant={isPrimaryFormat ? 'default' : 'outline'}
                          size="sm"
                          className={cn(
                            'h-7 gap-1 px-2.5 text-[11px] font-medium',
                            isPrimaryFormat && 'text-white',
                            !isPrimaryFormat && 'dark:border-slate-600 dark:text-slate-200'
                          )}
                          style={isPrimaryFormat ? { backgroundColor: moduleColor, borderColor: moduleColor } : undefined}
                          onClick={() =>
                            onSelectFlow ? onSelectFlow(flow.id, format) : openTour({ flowId: flow.id, format })
                          }
                        >
                          {formatIcon(info.icon)}
                          {info.label}
                          {isPrimaryFormat && <ArrowRight className="h-3 w-3" />}
                        </Button>
                      );
                    })
                    )}
                    {/* Chat format button — shown when the flow has a section_prompt */}
                    {flow.section_prompt && !isLocked && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1 px-2.5 text-[11px] font-medium dark:border-slate-600 dark:text-slate-200"
                        onClick={() => engine.selectChat(flow.id)}
                        title="Chat about this flow"
                      >
                        <MessageCircle className="h-3 w-3" />
                        Chat
                      </Button>
                    )}
                    {/* Dev+root: open this tour in edit mode — the runner
                        starts it with the inline editor already showing. Not
                        gated on prerequisites; locks only apply to learners. */}
                    {mediaRegenAvailable && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 px-2 text-[11px] font-medium text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                        title={`Open "${flow.label}" in edit mode (dev only)`}
                        onClick={() => startFlowForEdit(flow.id)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                    {/* Dev+root: regenerate this flow's video & narration on demand */}
                    {mediaRegenAvailable && !isLocked && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 px-2 text-[11px] font-medium text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                        disabled={regenFlowId === flow.id}
                        title="Regenerate this tour's video & narration (dev only)"
                        onClick={async () => {
                          setRegenFlowId(flow.id);
                          const result = await requestTourMediaRegen({ moduleKey, flowId: flow.id });
                          if (result.success) {
                            toast.success(result.message ?? `Regeneration queued for ${flow.label}`);
                          } else {
                            toast.error(result.error ?? 'Failed to queue regeneration');
                          }
                          setRegenFlowId(null);
                        }}
                      >
                        <RefreshCw className={cn('h-3 w-3', regenFlowId === flow.id && 'animate-spin')} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
